/**
 * Admin · Campus Ambassadors page.
 *
 * Three views, switched by `view` state:
 *   - `list`   → table of all CAs + program stats + "+ New CA" modal
 *   - `detail` → per-CA detail (funnel, full invitee table, payouts, audit log)
 *
 * The toggle `show_earnings_to_ca` on the detail page controls whether the
 * CA themselves can see earnings / payment info on their patron-side
 * Ambassador tab.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatDate } from "../lib/date";

const PKR = (n) => `₨ ${Number(n || 0).toLocaleString()}`;
const fmtDate = (iso) => formatDate(iso);

export default function AdminAmbassadors() {
  const [view, setView] = useState("list"); // list | detail
  const [selected, setSelected] = useState(null);
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/admin/ambassadors"),
        api.get("/admin/ambassadors/program-stats"),
      ]);
      setList(l.data || []);
      setStats(s.data || null);
    } catch (e) {
      toast.error("Failed to load ambassadors");
    }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  if (view === "detail" && selected) {
    return <AdminAmbassadorDetail ambassadorId={selected.id} onBack={() => { setView("list"); setSelected(null); reload(); }} />;
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="admin-ambassadors-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="overline text-mango">PROGRAM</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Campus Ambassadors</h1>
          <p className="text-sm text-neutral-600 mt-1">Paid acquisition channel. Each ambassador earns cash per qualified converted patron (signup + profile + first survey).</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="rounded-none border-2 border-black bg-mango text-black hover:bg-mango/90 font-bold uppercase tracking-wider hard-shadow" data-testid="ambassador-create-btn">+ New Campus Ambassador</Button>
      </div>

      {/* ── Stat strip ─────────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Stat label="Total CAs" value={stats.ambassadors?.total} hint={stats.ambassadors?.total_records !== stats.ambassadors?.total ? `${stats.ambassadors?.total_records} records across history` : null} />
          <Stat label="Active" value={stats.ambassadors?.active} accent="green" />
          <Stat label="Confirmed conversions" value={stats.conversions?.qualified + stats.conversions?.paid} />
          <Stat label="Owed (pending)" value={PKR(stats.money?.pending_payout_pkr)} accent="mango" />
          <Stat label="Paid lifetime" value={PKR(stats.money?.paid_lifetime_pkr)} />
        </div>
      )}

      {/* ── List table ─────────────────────────────────────────────── */}
      <div className="border-2 border-black bg-white">
        <table className="w-full text-sm">
          <thead className="bg-black text-mango">
            <tr>
              {["Name", "Email", "Campus", "Slug", "Status", "Signed up", "Confirmed", "Owed", "Paid", "Rate", ""].map((h) => (
                <th key={h} className="text-left px-3 py-3 overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-3 py-6 text-center text-neutral-400">Loading…</td></tr>}
            {!loading && list.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-neutral-400">No campus ambassadors yet — click <strong>+ New Campus Ambassador</strong> to add the first one.</td></tr>
            )}
            {list.map((r) => (
              <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => { setSelected(r); setView("detail"); }} data-testid={`ambassador-row-${r.id}`}>
                <td className="px-3 py-3 font-bold">
                  {r.name || "—"}
                  {r.historic_stints_count > 0 && (
                    <span
                      className="ml-2 text-[9px] tracking-wider bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded-sm align-middle"
                      title={`${r.historic_stints_count} other stint${r.historic_stints_count > 1 ? "s" : ""}: ${r.historic_stints.map(s => `${s.campus} (${s.status})`).join(", ")}`}
                      data-testid={`historic-stints-badge-${r.id}`}
                    >
                      +{r.historic_stints_count} HISTORIC
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs text-neutral-600">{r.email || "—"}</td>
                <td className="px-3 py-3 text-xs">{r.campus}</td>
                <td className="px-3 py-3 text-xs font-mono">{r.slug}</td>
                <td className="px-3 py-3"><StatusPill status={r.status} /></td>
                <td className="px-3 py-3 text-xs">{r.n_signed_up}</td>
                <td className="px-3 py-3 text-xs font-bold">{r.n_confirmed}</td>
                <td className="px-3 py-3 text-xs text-mango font-bold">{PKR(r.pending_pkr)}</td>
                <td className="px-3 py-3 text-xs text-emerald-600">{PKR(r.paid_pkr)}</td>
                <td className="px-3 py-3 text-xs font-mono">{PKR(r.rate_pkr)}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <RowActions ca={r} reload={reload} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateAmbassadorModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />}
    </div>
  );
}

function Stat({ label, value, accent, hint }) {
  const bg = accent === "green" ? "bg-emerald-50 border-emerald-600" : accent === "mango" ? "bg-mango/10 border-mango" : "bg-white border-neutral-300";
  return (
    <div className={`border-2 p-4 ${bg}`}>
      <div className="overline text-[10px] text-neutral-500">{label}</div>
      <div className="text-2xl font-black tracking-tighter mt-1 tabular-nums">{value ?? "—"}</div>
      {hint && <div className="text-[10px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: "border-emerald-700 text-emerald-700 bg-emerald-50",
    paused: "border-yellow-600 text-yellow-700 bg-yellow-50",
    terminated: "border-red-600 text-red-700 bg-red-50",
  };
  return <span className={`text-[10px] tracking-widest font-bold px-2 py-0.5 border ${map[status] || "border-neutral-300"}`}>{(status || "—").toUpperCase()}</span>;
}

function CreateAmbassadorModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", campus: "", rate_pkr: 20, slug: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  const doCreate = async (extra = {}) => {
    setBusy(true);
    try {
      await api.post("/admin/ambassadors", {
        ...form,
        rate_pkr: Number(form.rate_pkr) || 20,
        slug: form.slug || undefined,
        notes: form.notes || undefined,
        ...extra,
      });
      toast.success("Campus Ambassador created — welcome email queued.");
      onCreated();
    } catch (e) {
      const data = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof data === "object" && data?.code) {
        setConflict(data);
      } else {
        toast.error(typeof data === "string" ? data : "Failed to create ambassador");
      }
    }
    setBusy(false);
  };

  const submit = () => {
    if (!form.name || !form.email || !form.campus) { toast.error("Name, email and campus are required."); return; }
    setConflict(null);
    doCreate();
  };

  const reactivateExisting = async () => {
    if (!conflict?.existing_record?.id) return;
    setBusy(true);
    try {
      await api.patch(`/admin/ambassadors/${conflict.existing_record.id}`, { status: "active" });
      toast.success("Existing CA record reactivated.");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reactivation failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-black max-w-lg w-full p-6 hard-shadow" onClick={(e) => e.stopPropagation()} data-testid="ambassador-create-modal">
        <div className="overline text-mango">NEW</div>
        <h2 className="text-2xl font-black tracking-tighter mb-1">Add Campus Ambassador</h2>
        <p className="text-xs text-neutral-500 mb-5">Creates a patron account, generates a /cap/ link, and emails the ambassador a set-password link via Resend.</p>

        {conflict && (
          <div className="mb-5 border-2 border-amber-500 bg-amber-50 p-3" data-testid="ambassador-conflict-banner">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-800">Existing record detected</div>
            <div className="text-sm text-amber-900 mt-1">{conflict.message}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              {conflict.code === "user_already_ca" && (
                <Button onClick={reactivateExisting} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="ambassador-conflict-reactivate">
                  Reactivate existing
                </Button>
              )}
              {conflict.code === "user_has_terminated_stint" && (
                <Button onClick={() => doCreate({ force_fresh_stint: true })} disabled={busy} className="bg-black text-mango hover:bg-neutral-800 text-xs" data-testid="ambassador-conflict-fresh">
                  Create fresh stint anyway
                </Button>
              )}
              {conflict.code === "user_has_terminated_stint" && conflict.existing_record?.id && (
                <Button variant="outline" onClick={reactivateExisting} disabled={busy} className="text-xs" data-testid="ambassador-conflict-reactivate-old">
                  Reactivate old record
                </Button>
              )}
              <Button variant="ghost" onClick={() => setConflict(null)} className="text-xs">Cancel</Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="overline text-[10px]">Full name</label>
            <Input data-testid="ambassador-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" placeholder="Umair Iqbal" />
          </div>
          <div>
            <label className="overline text-[10px]">Email</label>
            <Input data-testid="ambassador-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" placeholder="umair@iba.edu.pk" />
          </div>
          <div>
            <label className="overline text-[10px]">University / campus</label>
            <Input data-testid="ambassador-form-campus" value={form.campus} onChange={(e) => setForm({ ...form, campus: e.target.value })} className="rounded-none" placeholder="IBA Karachi" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-[10px]">Per-conversion rate (₨)</label>
              <Input data-testid="ambassador-form-rate" type="number" min="0" value={form.rate_pkr} onChange={(e) => setForm({ ...form, rate_pkr: e.target.value })} className="rounded-none" />
            </div>
            <div>
              <label className="overline text-[10px]">Slug (optional)</label>
              <Input data-testid="ambassador-form-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="rounded-none font-mono" placeholder="auto-generate" />
            </div>
          </div>
          <div>
            <label className="overline text-[10px]">Internal notes (admin-only)</label>
            <Textarea data-testid="ambassador-form-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" placeholder="optional" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="ambassador-form-submit" className="rounded-none bg-black text-mango hover:bg-neutral-800" disabled={busy} onClick={submit}>{busy ? "Creating…" : "Create & Send Email"}</Button>
        </div>
      </div>
    </div>
  );
}

function AdminAmbassadorDetail({ ambassadorId, onBack }) {
  const [ca, setCa] = useState(null);
  const [invitees, setInvitees] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [busyAction, setBusyAction] = useState(null);
  const [showBackfill, setShowBackfill] = useState(false);

  const reload = async () => {
    try {
      const [c, inv, pay] = await Promise.all([
        api.get(`/admin/ambassadors/${ambassadorId}`),
        api.get(`/admin/ambassadors/${ambassadorId}/invitees`),
        api.get(`/admin/ambassadors/${ambassadorId}/payouts`),
      ]);
      setCa(c.data);
      setInvitees(inv.data || []);
      setPayouts(pay.data || []);
    } catch (e) {
      toast.error("Failed to load ambassador detail");
    }
  };
  useEffect(() => { reload(); }, [ambassadorId]);

  const updateField = async (patch) => {
    try {
      const { data } = await api.patch(`/admin/ambassadors/${ambassadorId}`, patch);
      setCa(data);
      toast.success("Saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const generateBatch = async () => {
    setBusyAction("gen");
    try {
      await api.post(`/admin/ambassadors/${ambassadorId}/payouts/generate`);
      toast.success("Draft payout batch created.");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusyAction(null);
  };

  const approveBatch = async (bid) => {
    try {
      await api.post(`/admin/ambassadors/payouts/${bid}/approve`, {});
      toast.success("Approved.");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const markPaid = async (bid) => {
    try {
      await api.post(`/admin/ambassadors/payouts/${bid}/mark-paid`, { paid_via: "easypaisa" });
      toast.success("Marked paid.");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const rejectConversion = async (convId) => {
    const reason = prompt("Reject reason?");
    if (!reason) return;
    try {
      await api.post(`/admin/ambassadors/${ambassadorId}/conversions/${convId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Conversion rejected.");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!ca) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="ambassador-detail-page">
      <button onClick={onBack} className="text-xs underline mb-4">← Back to list</button>
      <div className="border-2 border-black bg-white p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="overline text-mango">CAMPUS AMBASSADOR</div>
            <h1 className="text-3xl font-black tracking-tighter">{ca.name}</h1>
            <div className="text-sm text-neutral-600 mt-1">{ca.email} · {ca.phone_number || "no phone"} · {ca.campus}</div>
            <div className="text-xs text-neutral-500 mt-1">Slug: <span className="font-mono text-black">/cap/{ca.slug}</span> · Joined: {fmtDate(ca.user_joined_at)}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={ca.status} />
            {ca.status === "active" && <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => updateField({ status: "paused" })}>Pause</Button>}
            {ca.status === "paused" && <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => updateField({ status: "active" })}>Resume</Button>}
            {ca.status !== "terminated" && <Button size="sm" variant="ghost" className="rounded-none text-xs text-red-600" onClick={() => { if (confirm("Terminate this ambassador? They'll be demoted and their link will redirect to their peer code.")) updateField({ status: "terminated" }); }}>Terminate</Button>}
          </div>
        </div>

        {/* ── Funnel + money ─── */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-6">
          <Stat label="Invited" value={ca.n_invited} />
          <Stat label="Signed up" value={ca.n_signed_up} />
          <Stat label="Profile complete" value={ca.n_profile_complete} />
          <Stat label="Confirmed" value={ca.n_confirmed} accent="green" />
          <Stat label="Owed" value={PKR(ca.pending_pkr)} accent="mango" />
          <Stat label="Paid" value={PKR(ca.paid_pkr)} />
        </div>

        {/* ── Editable controls ─── */}
        <div className="grid md:grid-cols-3 gap-4 mt-6 border-t border-neutral-200 pt-5">
          <div>
            <label className="overline text-[10px]">Per-conversion rate (₨)</label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="ambassador-edit-rate" type="number" min="0" defaultValue={ca.rate_pkr} className="rounded-none" id={`rate-${ca.id}`} />
              <Button size="sm" className="rounded-none bg-black text-mango" onClick={() => updateField({ rate_pkr: Number(document.getElementById(`rate-${ca.id}`).value) })}>Save</Button>
            </div>
            <div className="text-[10px] text-neutral-500 mt-1">Applies only to FUTURE conversions. Existing accruals are frozen at their rate.</div>
          </div>
          <div>
            <label className="overline text-[10px]">Show earnings on CA&apos;s dashboard</label>
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={() => updateField({ show_earnings_to_ca: !ca.show_earnings_to_ca })}
                data-testid="ambassador-toggle-earnings"
                className={`relative inline-flex h-6 w-11 items-center border-2 border-black ${ca.show_earnings_to_ca ? "bg-mango" : "bg-neutral-200"}`}
              >
                <span className={`inline-block h-4 w-4 transform bg-black transition ${ca.show_earnings_to_ca ? "translate-x-5" : "translate-x-1"}`} />
              </button>
              <span className="text-xs">{ca.show_earnings_to_ca ? "ON — CA sees earnings + payouts" : "OFF — earnings hidden from CA"}</span>
            </div>
          </div>
          <div>
            <label className="overline text-[10px]">Internal notes</label>
            <Textarea data-testid="ambassador-edit-notes" rows={2} defaultValue={ca.notes || ""} className="rounded-none mt-1" id={`notes-${ca.id}`} />
            <Button size="sm" className="rounded-none bg-black text-mango mt-1" onClick={() => updateField({ notes: document.getElementById(`notes-${ca.id}`).value })}>Save notes</Button>
          </div>
        </div>
      </div>

      {/* ── Backfill banner (only when not yet backfilled) ─── */}
      {!ca.backfilled_at && (
        <div className="border-2 border-mango bg-mango/10 p-4 mb-6 flex items-center justify-between gap-4" data-testid="backfill-banner">
          <div>
            <div className="overline text-mango font-bold">PRIOR REFERRALS</div>
            <div className="text-sm font-medium mt-1">This patron has a referral history. Backfill brings their earlier invitees into the CA dashboard with their current funnel stage.</div>
            <div className="text-xs text-neutral-600 mt-1">Choose one of three policies — cash-with-points-reversal, cash-on-top, or visibility-only.</div>
          </div>
          <Button onClick={() => setShowBackfill(true)} className="rounded-none bg-black text-mango hover:bg-neutral-800 font-bold uppercase tracking-wider whitespace-nowrap" data-testid="backfill-open">Backfill referrals</Button>
        </div>
      )}
      {ca.backfilled_at && (
        <div className="border border-neutral-300 bg-neutral-50 p-3 mb-6 text-xs text-neutral-600" data-testid="backfill-done-banner">
          ✓ Prior referrals were backfilled on <strong>{fmtDate(ca.backfilled_at)}</strong> using policy <strong>{ca.backfilled_policy}</strong>
          {ca.backfill_reversed_points ? ` · ${ca.backfill_reversed_points.toLocaleString()} points reversed in ledger` : ""}
          {ca.backfill_counts ? ` · ${ca.backfill_counts.total} conversions migrated (${ca.backfill_counts.confirmed} confirmed)` : ""}.
        </div>
      )}

      {/* ── Invitees table ─── */}
      <div className="border-2 border-black bg-white mb-6">
        <div className="bg-black text-mango px-4 py-2 flex items-center justify-between">
          <h2 className="font-bold text-sm tracking-wider">INVITEES ({invitees.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 border-b-2 border-black">
              <tr>
                {["Name", "Email", "Phone", "Age", "Region", "Signed up", "Profile", "Survey", "Status", "Rate", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-2 overline whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invitees.length === 0 && <tr><td colSpan={11} className="px-3 py-6 text-center text-neutral-400">No invitees yet.</td></tr>}
              {invitees.map((i) => (
                <tr key={i.conversion_id} className="border-b border-neutral-100" data-testid={`invitee-row-${i.user_id}`}>
                  <td className="px-3 py-2 font-bold">{i.name || "—"}</td>
                  <td className="px-3 py-2 text-neutral-600">{i.email}</td>
                  <td className="px-3 py-2 text-neutral-600">{i.phone_number || "—"}</td>
                  <td className="px-3 py-2">{i.age_band || "—"}</td>
                  <td className="px-3 py-2">{i.region || "—"}</td>
                  <td className="px-3 py-2 text-neutral-500">{fmtDate(i.signed_up_at)}</td>
                  <td className="px-3 py-2">{i.profile_complete_at ? "✓" : "—"}</td>
                  <td className="px-3 py-2">{i.first_survey_at ? "✓" : "—"}</td>
                  <td className="px-3 py-2"><ConvStatus s={i.status} /></td>
                  <td className="px-3 py-2 font-mono">{PKR(i.rate_at_conversion_pkr)}</td>
                  <td className="px-3 py-2">{i.status !== "paid" && i.status !== "rejected" && <button onClick={() => rejectConversion(i.conversion_id)} className="text-[10px] text-red-600 underline">Reject</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Payouts ─── */}
      <div className="border-2 border-black bg-white">
        <div className="bg-black text-mango px-4 py-2 flex items-center justify-between">
          <h2 className="font-bold text-sm tracking-wider">PAYOUT BATCHES ({payouts.length})</h2>
          <Button size="sm" className="rounded-none bg-mango text-black hover:bg-mango/80" disabled={busyAction === "gen"} onClick={generateBatch} data-testid="ambassador-generate-batch">+ Generate {ca.pending_pkr > 0 ? PKR(ca.pending_pkr) : "current"} payout</Button>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-neutral-50 border-b-2 border-black">
            <tr>
              {["Period", "Conv.", "Total", "Status", "Generated", "Approved", "Paid", "Actions"].map((h) => (
                <th key={h} className="text-left px-3 py-2 overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payouts.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-neutral-400">No payout batches yet.</td></tr>}
            {payouts.map((b) => (
              <tr key={b.id} className="border-b border-neutral-100">
                <td className="px-3 py-2 font-mono">{b.period_label}</td>
                <td className="px-3 py-2">{b.n_conversions}</td>
                <td className="px-3 py-2 font-bold text-mango">{PKR(b.total_pkr)}</td>
                <td className="px-3 py-2"><BatchStatus s={b.status} /></td>
                <td className="px-3 py-2 text-neutral-500">{fmtDate(b.generated_at)}</td>
                <td className="px-3 py-2 text-neutral-500">{fmtDate(b.approved_at)}</td>
                <td className="px-3 py-2 text-neutral-500">{fmtDate(b.paid_at)}</td>
                <td className="px-3 py-2">
                  {b.status === "draft" && <button onClick={() => approveBatch(b.id)} className="text-[10px] underline mr-2">Approve</button>}
                  {(b.status === "draft" || b.status === "approved") && <button onClick={() => markPaid(b.id)} className="text-[10px] underline text-emerald-600 mr-2">Mark paid</button>}
              <a href={`${api.defaults.baseURL}/admin/ambassadors/payouts/${b.id}/export.csv`} className="text-[10px] underline" target="_blank" rel="noreferrer">CSV</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showBackfill && <BackfillDialog caId={ambassadorId} ca={ca} onClose={() => setShowBackfill(false)} onDone={() => { setShowBackfill(false); reload(); }} />}
    </div>
  );
}

function BackfillDialog({ caId, ca, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [policy, setPolicy] = useState("cash_with_reversal");
  const [rate, setRate] = useState(ca?.rate_pkr || 20);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/admin/ambassadors/${caId}/backfill/preview`)
      .then((r) => { setPreview(r.data); setRate(r.data.default_rate_pkr || 20); })
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load preview"));
  }, [caId]);

  const submit = async () => {
    if (!preview) return;
    if (preview.counts.total === 0) { toast.error("No referrals to backfill"); return; }
    const policyLabel = {
      visibility_only: "import for visibility (₨0 on every conversion, no points reversed)",
      cash_with_reversal: `award ₨${rate} per confirmed conversion AND reverse ${preview.points.capped_reversal.toLocaleString()} points`,
      cash_on_top: `award ₨${rate} per confirmed conversion (points stay)`,
    }[policy];
    if (!window.confirm(`Confirm backfill:\n\n${policyLabel}\n\nThis is final and cannot be re-run for this CA.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/ambassadors/${caId}/backfill`, {
        policy,
        rate_pkr: policy === "visibility_only" ? 0 : Number(rate),
      });
      toast.success(`Backfill done — ${data.conv_created} conversions migrated · ${data.points_reversed.toLocaleString()} points reversed.`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    }
    setBusy(false);
  };

  if (err) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white border-2 border-black max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
          <h2 className="text-xl font-black tracking-tighter mb-2">Couldn&apos;t load preview</h2>
          <p className="text-sm text-red-600">{err}</p>
          <Button onClick={onClose} className="rounded-none mt-4">Close</Button>
        </div>
      </div>
    );
  }
  if (!preview) {
    return <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"><div className="text-mango">Loading preview…</div></div>;
  }

  const wouldExceed = preview.points.would_exceed_balance;
  const cashPaid = (policy === "visibility_only" ? 0 : Number(rate) || 0) * preview.counts.confirmed;
  const projected = policy === "cash_with_reversal" ? preview.points.projected_balance : preview.points.current_balance;
  const reversedShown = policy === "cash_with_reversal" ? preview.points.capped_reversal : 0;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white border-2 border-black max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="backfill-modal">
        {/* Header */}
        <div className="bg-black text-mango px-6 py-4">
          <div className="overline">BACKFILL PRIOR PEER REFERRALS</div>
          <h2 className="text-2xl font-black tracking-tighter text-white">{ca.name}</h2>
          <div className="text-xs text-neutral-300 mt-1">Total prior referrals: {preview.counts.total} · {preview.counts.confirmed} confirmed · {preview.counts.profile_only} profile-only · {preview.counts.signup_only} signup-only</div>
        </div>

        <div className="p-6 space-y-5">
          {/* Policy radios */}
          <div className="space-y-2">
            <div className="overline text-[10px] text-neutral-500">Choose a policy</div>
            <Policy value="cash_with_reversal" selected={policy} onChange={setPolicy}
              title="Award cash AND reverse previously-credited points"
              body="One-for-one swap. Cash credited per confirmed conversion. Previously-awarded referral_bonus rows are reversed in the patron's ledger (capped at current balance — never goes negative). Cleanest model; recommended."
              recommended />
            <Policy value="cash_on_top" selected={policy} onChange={setPolicy}
              title="Award cash on top (no points reversal)"
              body="Patron keeps her old referral points AND earns cash on confirmed conversions. Generous; slight double-pay on legacy invitees but a good recognition gesture." />
            <Policy value="visibility_only" selected={policy} onChange={setPolicy}
              title="Import for visibility only — ₨0 on every backfilled conversion"
              body="Bring the historical pipeline onto the CA's dashboard but no cash is owed and no points are reversed. Cash starts fresh on FUTURE conversions only." />
          </div>

          {/* Rate input */}
          {policy !== "visibility_only" && (
            <div className="border border-neutral-300 p-3 bg-neutral-50">
              <label className="overline text-[10px] text-neutral-500">Backfill rate per confirmed (₨)</label>
              <Input data-testid="backfill-rate-input" type="number" min="0" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-none mt-1 max-w-[120px]" />
              <div className="text-[10px] text-neutral-500 mt-1">Default = the CA&apos;s current per-conversion rate. You can override for this cohort only.</div>
            </div>
          )}

          {/* Live preview */}
          <div className="border-2 border-black bg-white">
            <div className="bg-mango/15 px-4 py-2 overline font-bold">PREVIEW</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 p-4 text-sm">
              <div className="text-neutral-500">Confirmed (cycle complete)</div>
              <div className="text-right font-mono">{preview.counts.confirmed}</div>
              <div className="text-neutral-500">Profile complete, no survey</div>
              <div className="text-right font-mono">{preview.counts.profile_only}</div>
              <div className="text-neutral-500">Signed up only</div>
              <div className="text-right font-mono">{preview.counts.signup_only}</div>
              <div className="col-span-2 border-t border-neutral-200 mt-1"></div>
              <div className="font-bold">Cash credited to CA</div>
              <div className="text-right font-mono font-bold text-mango">{PKR(cashPaid)}</div>
              {policy === "cash_with_reversal" && (
                <>
                  <div className="font-bold">Points to reverse</div>
                  <div className="text-right font-mono font-bold text-red-600">−{reversedShown.toLocaleString()}</div>
                  <div className="text-neutral-500">Patron&apos;s current balance</div>
                  <div className="text-right font-mono">{preview.points.current_balance.toLocaleString()}</div>
                  <div className="font-bold">Patron&apos;s new balance</div>
                  <div className="text-right font-mono font-bold">{projected.toLocaleString()}</div>
                  {wouldExceed && (
                    <div className="col-span-2 text-[11px] text-yellow-700 bg-yellow-50 border border-yellow-300 px-2 py-1 mt-1">
                      ⚠ Total previously-credited points ({preview.points.to_reverse.toLocaleString()}) exceed current balance. Reversal is capped at <strong>{preview.points.capped_reversal.toLocaleString()}</strong>. The patron will not go below zero; the un-reversable portion remains hers.
                    </div>
                  )}
                </>
              )}
              {policy === "cash_on_top" && (
                <div className="col-span-2 text-[11px] text-neutral-600 bg-neutral-50 border border-neutral-200 px-2 py-1 mt-1">
                  Patron keeps all <strong>{preview.points.current_balance.toLocaleString()}</strong> existing points. She is paid {PKR(cashPaid)} on top.
                </div>
              )}
              {policy === "visibility_only" && (
                <div className="col-span-2 text-[11px] text-neutral-600 bg-neutral-50 border border-neutral-200 px-2 py-1 mt-1">
                  Backfilled conversions accrue at ₨0. Patron keeps all existing points. Future conversions on the CA link earn at the CA&apos;s normal rate.
                </div>
              )}
            </div>
          </div>

          {/* Action footer */}
          <div className="flex items-center justify-between border-t border-neutral-200 pt-4">
            <div className="text-[10px] text-neutral-500 italic">This action is logged and cannot be re-run.</div>
            <div className="flex gap-2">
              <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button data-testid="backfill-confirm" className="rounded-none bg-mango text-black hover:bg-mango/80 font-bold tracking-wider" disabled={busy || preview.counts.total === 0} onClick={submit}>
                {busy ? "Working…" : `Backfill ${preview.counts.total} referrals`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Policy({ value, selected, onChange, title, body, recommended }) {
  const isSel = selected === value;
  return (
    <label className={`block border-2 ${isSel ? "border-black bg-mango/10" : "border-neutral-300 hover:border-neutral-500"} p-3 cursor-pointer transition-colors`} data-testid={`backfill-policy-${value}`}>
      <div className="flex items-start gap-3">
        <input type="radio" name="backfill-policy" value={value} checked={isSel} onChange={() => onChange(value)} className="mt-1" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold">{title}</span>
            {recommended && <span className="text-[9px] tracking-widest font-bold px-1.5 py-0.5 bg-mango text-black border border-black">RECOMMENDED</span>}
          </div>
          <div className="text-xs text-neutral-600 mt-1 leading-snug">{body}</div>
        </div>
      </div>
    </label>
  );
}

function ConvStatus({ s }) {
  const m = {
    pending: ["text-neutral-500 border-neutral-300 bg-white", "PENDING"],
    qualified: ["text-mango border-mango bg-mango/10", "QUALIFIED"],
    paid: ["text-emerald-700 border-emerald-700 bg-emerald-50", "PAID"],
    rejected: ["text-red-600 border-red-600 bg-red-50", "REJECTED"],
    reversed: ["text-red-700 border-red-700 bg-red-50", "REVERSED"],
  }[s] || ["text-neutral-400 border-neutral-300", s];
  return <span className={`text-[9px] font-bold tracking-widest px-1.5 py-0.5 border ${m[0]}`}>{m[1]}</span>;
}
function BatchStatus({ s }) {
  const m = {
    draft: ["text-neutral-600 border-neutral-300", "DRAFT"],
    approved: ["text-mango border-mango bg-mango/10", "APPROVED"],
    paid: ["text-emerald-700 border-emerald-700 bg-emerald-50", "PAID"],
  }[s] || ["text-neutral-400 border-neutral-300", s];
  return <span className={`text-[9px] font-bold tracking-widest px-1.5 py-0.5 border ${m[0]}`}>{m[1]}</span>;
}



/**
 * Inline row-actions cluster for the CA list table. Surfaces "Edit" always
 * and "Remove" only when `paid_pkr === 0` (rule lives on backend too —
 * the FE just hides the button for the obvious case to avoid confusion).
 */
function RowActions({ ca, reload }) {
  const [showEdit, setShowEdit] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const canRemove = (ca.paid_pkr || 0) === 0;
  return (
    <>
      <button
        onClick={() => setShowEdit(true)}
        data-testid={`edit-ca-${ca.id}`}
        className="px-2 py-1 text-[10px] tracking-wider border border-neutral-300 hover:border-black hover:bg-neutral-50"
      >EDIT</button>
      {canRemove ? (
        <button
          onClick={() => setShowRemove(true)}
          data-testid={`remove-ca-${ca.id}`}
          className="ml-1 px-2 py-1 text-[10px] tracking-wider border border-red-300 text-red-700 hover:bg-red-50"
        >REMOVE</button>
      ) : (
        <span
          className="ml-1 px-2 py-1 text-[10px] tracking-wider border border-neutral-200 text-neutral-400 cursor-not-allowed"
          title={`Cannot Remove — Rs ${ca.paid_pkr} already paid. Use Terminate instead to preserve the audit trail.`}
        >REMOVE</span>
      )}
      {showEdit && <EditCAModal ca={ca} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); reload(); }} />}
      {showRemove && <RemoveCAModal ca={ca} onClose={() => setShowRemove(false)} onRemoved={() => { setShowRemove(false); reload(); }} />}
    </>
  );
}

/**
 * Edit CA — campus, rate, status, notes, earnings visibility. Name + email
 * are read-only (they mirror the patron's `users` record).
 */
function EditCAModal({ ca, onClose, onSaved }) {
  const [form, setForm] = useState({
    campus: ca.campus || "",
    rate_pkr: ca.rate_pkr || 20,
    status: ca.status || "active",
    notes: ca.notes || "",
    show_earnings_to_ca: !!ca.show_earnings_to_ca,
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Send only the diff so the audit log doesn't record no-op events.
      const diff = {};
      if (form.campus.trim() !== (ca.campus || "")) diff.campus = form.campus.trim();
      if (Number(form.rate_pkr) !== Number(ca.rate_pkr)) diff.rate_pkr = Number(form.rate_pkr);
      if (form.status !== ca.status) diff.status = form.status;
      if (form.notes !== (ca.notes || "")) diff.notes = form.notes;
      if (form.show_earnings_to_ca !== !!ca.show_earnings_to_ca) diff.show_earnings_to_ca = form.show_earnings_to_ca;
      if (Object.keys(diff).length === 0) { toast.info("No changes to save."); setSaving(false); return; }
      await api.patch(`/admin/ambassadors/${ca.id}`, diff);
      toast.success("Ambassador updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border-2 border-black hard-shadow p-6 max-w-md w-full" data-testid="edit-ca-modal">
        <h2 className="text-2xl font-black tracking-tighter mb-1">Edit campus ambassador</h2>
        <div className="text-xs text-neutral-500 mb-4">{ca.name} · {ca.email}</div>
        <div className="space-y-3">
          <Field label="Campus">
            <Input value={form.campus} onChange={(e) => setForm({ ...form, campus: e.target.value })} data-testid="edit-ca-campus" />
          </Field>
          <Field label="Rate (Rs per confirmed conversion)" hint="New rate applies only to future conversions — past confirmed conversions keep their historical rate.">
            <Input type="number" min="0" value={form.rate_pkr} onChange={(e) => setForm({ ...form, rate_pkr: e.target.value })} data-testid="edit-ca-rate" />
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 text-sm" data-testid="edit-ca-status">
              <option value="active">Active</option>
              <option value="paused">Paused (temporary halt — reversible)</option>
              <option value="terminated">Terminated (permanent — record kept for audit)</option>
            </select>
          </Field>
          <Field label="Notes (admin-only)">
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-ca-notes" />
          </Field>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={form.show_earnings_to_ca} onChange={(e) => setForm({ ...form, show_earnings_to_ca: e.target.checked })} data-testid="edit-ca-show-earnings" />
            <span>Show earnings + payment info to this CA on their dashboard</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="edit-ca-save">{saving ? "Saving…" : "Save changes"}</Button>
        </div>
      </form>
    </div>
  );
}

/**
 * Remove CA — soft-deletes the campus_ambassadors row, untouching the
 * underlying patron account. Destructive enough that we make the admin
 * type the slug to confirm. Backend also re-checks paid_pkr === 0.
 */
function RemoveCAModal({ ca, onClose, onRemoved }) {
  const [confirm, setConfirm] = useState("");
  const [removing, setRemoving] = useState(false);
  const canSubmit = confirm.trim() === (ca.slug || "");
  const submit = async () => {
    if (!canSubmit) return;
    setRemoving(true);
    try {
      await api.post(`/admin/ambassadors/${ca.id}/remove`);
      toast.success(`${ca.name || "Ambassador"} removed — patron account preserved`);
      onRemoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
    setRemoving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-red-700 hard-shadow p-6 max-w-md w-full" data-testid="remove-ca-modal">
        <h2 className="text-2xl font-black tracking-tighter mb-1">Remove campus ambassador</h2>
        <p className="text-sm text-neutral-700 mt-3 leading-relaxed">
          This will hide <strong>{ca.name}</strong>&apos;s CA record from the
          dashboard. Their patron account, points, profile, and any past
          confirmed conversions stay intact.
        </p>
        <p className="text-xs text-neutral-500 mt-2">
          Use this for promotions that were a mistake. Use <strong>Terminate</strong> (via Edit → Status) if the CA tenure ended normally — that preserves the audit trail.
        </p>
        <div className="mt-4">
          <div className="text-xs text-neutral-600 mb-1">Type the slug <code className="font-mono bg-neutral-100 px-1 py-0.5">{ca.slug}</code> to confirm:</div>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={ca.slug} data-testid="remove-ca-confirm-input" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || removing} className="bg-red-600 hover:bg-red-700 text-white" data-testid="remove-ca-confirm">
            {removing ? "Removing…" : "Remove ambassador"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-neutral-500 mt-1">{hint}</div>}
    </label>
  );
}
